import {produce} from 'immer';
import {useSetAtom} from 'jotai';
import {orderBy, unionBy} from 'lodash';
import {HTMLAttributes, useEffect, useMemo, useRef, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {HighlightText} from '@/components/HighlightText';
import {SearchIcon} from '@/components/Icons';
import {TextField} from '@/components/TextField';
import {Typography, typographyStyle} from '@/components/Typography';
import {useBackgroundClick} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {
  AddressItemType, AddressSearchHistoryItem, AddressSearchItem, SubwaySearchItem
} from '@/map/@types';
import {useSearchAddress} from '@/map/api';
import {mapAddressSearchHistoryAtom} from '@/map/atom';
import {AddressKey} from '@/map/enums';
import {
  convertToCoordFromPointCoords, getAddressItemKeyword, getAddressMainKey, getAddressSubKey,
  getSubwayLineColor, getZoomLevel
} from '@/map/utils';
import {Flexbox, Section} from '@/styled-components';
import {palette} from '@/styles';
import {useQueryClient} from '@tanstack/react-query';
import {LocationMarker} from '../../Markers/LocationMarker';
import {AddrBadge} from '../StyledComponents';
import {SearchHistoryList} from './HistoryList';

interface Inputs {
  address: string;
}

interface IProps {
  map?: naver.maps.Map;
}

interface Point {
  name?: string;
  origin: AddressItemType;
}

export const Search = ({map}: IProps) => {
  const {register, watch, reset, handleSubmit} = useForm<Inputs>();
  const [keyword, setKeyword] = useState<string>('');
  const {data} = useSearchAddress(keyword);
  const [focused, setFocused] = useState<boolean>(false);
  const [point, setPoint] = useState<Point>();
  const setHistory = useSetAtom(mapAddressSearchHistoryAtom);
  const queryClient = useQueryClient();

  const tracking = useGA4WithParams();

  const sortedSubway = useMemo(
    () =>
      data?.subway
        ? orderBy(data.subway, ['name', 'line'], ['asc', 'asc'])
        : [],
    [data?.subway]
  );

  const duplicateFreeAddr = useMemo(
    () =>
      data?.address
        ? unionBy(data?.address, (item) => getAddressItemKeyword(item, keyword))
        : [],
    [data?.address, keyword]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const hasSearchResult =
    !!data && (!!sortedSubway?.length || !!duplicateFreeAddr?.length);

  const pointPosition = useMemo(() => {
    return point?.origin?.location?.coordinates
      ? convertToCoordFromPointCoords(point.origin.location.coordinates)
      : undefined;
  }, [point]);

  useBackgroundClick(containerRef, () => {
    reset();
    setFocused(false);
  });

  const setSearchHistory = (historyItem: AddressSearchHistoryItem) => {
    if (!historyItem) {
      return;
    }

    setHistory(
      produce((draft) => {
        const item: AddressSearchHistoryItem = {
          createdTime: new Date().getTime(),
          ...historyItem,
        };

        const dupIndex = draft.findIndex((i: AddressSearchHistoryItem) => {
          if (item.keyword) {
            return i.keyword === item.keyword;
          } else {
            return (
              !!i.mainKey &&
              !!item.mainKey &&
              (i.origin as AddressSearchItem)[i.mainKey] ===
                (item.origin as AddressSearchItem)[item.mainKey]
            );
          }
        });

        if (dupIndex >= 0) {
          draft.splice(dupIndex, 1);
        }

        draft.unshift(item);
        draft.slice(0, 10);
      })
    );
  };

  const onSubmit: SubmitHandler<Inputs> = () => {
    onSearchEnter();
  };

  const onChange = (value: string) => {
    setKeyword(value);
  };

  const setMarkerPoint = (item: AddressItemType) => {
    const markerPoint: Point = {
      origin: item,
    };

    if ('line' in item && item.location) {
      markerPoint.name = `${item.name} ${item.line}`;
    } else {
      const addressItem = item as AddressSearchItem;
      markerPoint.name = getAddressItemKeyword(addressItem, keyword);
    }

    setPoint(markerPoint);
  };

  const findSearchItem = (
    item: AddressItemType,
    opt?: {preventSaveHistory?: boolean; clearPrevResult?: boolean}
  ) => {
    if (opt?.clearPrevResult) {
      queryClient.removeQueries({queryKey: ['search-address', keyword]});
      setFocused(false);
    }

    setMarkerPoint(item);

    const historyItem: AddressSearchHistoryItem = {
      origin: item,
    };

    if (!('line' in item) && item.location) {
      const addressItem = item as AddressSearchItem;

      historyItem.mainKey = getAddressMainKey(addressItem, keyword);
      historyItem.subKey = getAddressSubKey(addressItem, keyword);
    }

    if (!(opt?.preventSaveHistory && !historyItem.mainKey)) {
      setSearchHistory(historyItem);
    }
  };

  const onSearchEnter = () => {
    setSearchHistory({keyword});
    tracking('search_address', {keyword});

    const first = sortedSubway.length
      ? sortedSubway[0]
      : duplicateFreeAddr?.[0];

    if (first) {
      findSearchItem(first, {
        preventSaveHistory: true,
        clearPrevResult: true,
      });
    }
  };

  const onHistoryItemClick = (historyItem: AddressSearchHistoryItem) => {
    if (!historyItem) {
      return;
    }

    if (historyItem.keyword) {
      setKeyword(historyItem.keyword);
    } else if (historyItem.origin) {
      if ('line' in historyItem.origin) {
        setKeyword(historyItem.origin.name);
      } else {
        setKeyword((historyItem.origin as any)[historyItem.mainKey as any]);
      }

      setMarkerPoint(historyItem.origin);
      setSearchHistory(historyItem);
    }
  };

  const onClickSearchItem = (item: AddressItemType) => () => {
    findSearchItem(item);
  };

  useEffect(() => {
    const subscription = watch((values) => onChange(values?.['address'] || ''));

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (pointPosition && map && naver.maps) {
      map.morph(pointPosition, getZoomLevel(50));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointPosition]);

  return (
    <Root ref={containerRef}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          type="text"
          placeholder="지역 이름, 주소, 지하철 검색"
          value={keyword}
          autoComplete="off"
          icon={SearchIcon}
          {...register('address')}
          onClear={reset}
          onFocus={() => setFocused(true)}
        />
      </form>
      {hasSearchResult && (
        <List>
          {sortedSubway?.map((i) => (
            <SubwayItem
              key={'subway-' + i.line + i.name}
              item={i}
              keyword={keyword}
              onClick={onClickSearchItem(i)}
            />
          ))}
          {duplicateFreeAddr?.map((i, idx) => (
            <AddressItem
              key={'keyword-' + idx}
              item={i}
              keyword={keyword}
              onClick={onClickSearchItem(i)}
            />
          ))}
        </List>
      )}
      {focused && !keyword?.length && !hasSearchResult && (
        <List>
          <SearchHistoryList onClick={onHistoryItemClick} />
        </List>
      )}
      {!!point && !!pointPosition && !!point?.name && (
        <LocationMarker
          map={map}
          address={point.name}
          position={pointPosition}
          hasShadow
          onClose={() => setPoint(undefined)}
          onCopy={() => undefined}
        />
      )}
    </Root>
  );
};

const SubwayItem = ({
  item,
  keyword,
  ...props
}: {
  item: SubwaySearchItem;
  keyword: string;
} & HTMLAttributes<HTMLDivElement>) => {
  return (
    <Item {...props}>
      <Typography variant="medium14" as="div" ellipsis>
        <Typography color={getSubwayLineColor(item)}>{item.line}</Typography>
        <Typography color={palette.gray400}>{' | '}</Typography>
        <HighlightText
          query={keyword}
          text={getAddressItemKeyword(item) || ''}
          highlightColor={palette.blue100}
        />
      </Typography>
    </Item>
  );
};

const AddressItem = ({
  item,
  keyword,
  ...props
}: {
  item: AddressSearchItem;
  keyword: string;
} & HTMLAttributes<HTMLDivElement>) => {
  const subKey = getAddressSubKey(item, keyword);
  const address = getAddressItemKeyword(item, keyword);

  return (
    <Item {...props}>
      <Typography variant="medium14" as="div" ellipsis>
        <HighlightText
          query={keyword}
          text={address || ''}
          highlightColor={palette.blue100}
        />
      </Typography>
      {!!subKey && (
        <Section spacing={1}>
          <Flexbox>
            <AddrBadge>
              {subKey === AddressKey.roadAddressName ? '도로명' : '지번'}
            </AddrBadge>
            <Typography
              variant="medium12"
              as="div"
              color={palette.textSecondary}
            >
              {item[subKey]}
            </Typography>
          </Flexbox>
        </Section>
      )}
    </Item>
  );
};

const Root = styled.div`
  position: relative;
  width: 100%;
  ${{...typographyStyle.medium14}};
`;

const List = styled.div`
  position: absolute;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid ${palette.gray300};
  background-color: ${palette.white};
  width: 100%;
  font-weight: 500;
  z-index: 1;
`;

const Item = styled.div`
  padding: 7px 4px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: ${palette.gray100};
  }
`;
